import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { galleryFetch } from "../../../services/api/Gallery";
import PageHeader from "../../paragraphHeader";
import { ArrowRight } from "lucide-react";
import { Button } from "../../ui/button";
import NotFoundImage from "../../NotFound";

interface GalleryFolder {
  name: string;
  title: string;
  slug: string;
  description: string;
  type: string;
  thumbnail: string;
  url: string;

  parent?: {
    id: number;
    name: string;
    slug: string;
  } | null;
}
interface GalleryCategory {
  id: number;
  name: string;
  slug: string;
  title: string;
  background_image: string | null;
  parent: {
    id: number;
    name: string;
    slug: string;
  } | null;
}

const GalleryPage: React.FC = () => {
  const [folders, setFolders] = useState<GalleryFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const [mediaRes, categoryRes] = await Promise.all([
          galleryFetch("media"),
          galleryFetch("gallery/categories"),
        ]);

        setFolders(mediaRes?.data?.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const categories = Array.from(
    new Map(
      folders
        .filter((item) => item.parent)
        .map((item) => [item.parent!.id, item.parent!]),
    ).values(),
  );
  const filteredFolders =
    selectedCategory === "all"
      ? folders
      : folders.filter((item) => item.parent?.id === Number(selectedCategory));
  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={
          <h1 className="font-semibold text-[#1F4D8F] uppercase">Gallery</h1>
        }
        subtitle={
          <>
            Explore{" "}
            <span className="text-blue-500 font-bold">Sports Events</span>
          </>
        }
        paragraph="Explore Haryana's premier sports events and championships."
        backgroundImage=""
        align="center"
        showOlympicsIcon={true}
      />

      <div className="max-w-7xl mx-auto p-6">
        {/* HEADING */}
        <div className="mb-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left Side */}
            <div>
              <h2 className="text-3xl font-bold text-slate-800">
                Explore Our Gallery
              </h2>

              <div className="w-20 h-1 bg-yellow-400 rounded-full mt-3"></div>
            </div>

            {/* Right Side */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-4 py-2 rounded-full ${
                  selectedCategory === "all"
                    ? "bg-yellow-500 text-white"
                    : "bg-white border border-gray-200"
                }`}
              >
                All
              </button>

              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id.toString())}
                  className={`px-4 py-2 rounded-full ${
                    selectedCategory === category.id.toString()
                      ? "bg-yellow-500 text-white"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        {loading ? (
          <p className="text-center">Loading...</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 ">
            {filteredFolders.map((item, index) => (
              <Link
                key={index}
                to={`/gallery/${item.slug}`}
                className="bg-white rounded-xl shadow hover:shadow-lg transition overflow-hidden group"
              >
                {/* Image */}
                <div className="aspect-square overflow-hidden shadow-xl">
                  <img
                    src={item.thumbnail}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                  />
                </div>

                {/* Content */}
                <div className="p-4 text-black">
                  <p className="font-semibold text-xl capitalize mb-2 text-center">
                    {item.title.replace(/-/g, " ")}
                  </p>
                  {item.description && (
                    <p
                      className=" mb-4 text-center line-clamp-3"
                      dangerouslySetInnerHTML={{
                        __html: item.description,
                      }}
                    />
                  )}

                  {/* Button */}
                  <Button
                    size="lg"
                    className="w-full bg-yellow-500 hover:bg-yellow-700 cursor-pointer flex items-center justify-center gap-2"
                  >
                    View Gallery
                    <ArrowRight size={18} />
                  </Button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryPage;
